import { useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiWrite, list, type Role } from '../lib/api'
import { useAuth } from '../auth'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { thaiDateTime } from '../lib/format'

/**
 * Threshold alerting with escalation and silencing (part 36).
 *
 * Every number on this page comes from the server. The page never re-derives a level: it shows
 * the persisted state, the pending consecutive-breach counter and the durable event row that the
 * evaluation wrote. A silenced rule is deliberately visible as "ระงับการแจ้งเตือน" rather than
 * disappearing from the queue, because a breach during a maintenance window is still evidence.
 *
 * This surface is not statistical anomaly detection. It compares one sample with two operator
 * thresholds; baselines and z-scores live on the anomaly page.
 */

type Level = 'ok' | 'warning' | 'critical'
type Comparison = 'above' | 'below'
type EventKind = 'warning' | 'critical' | 'escalated' | 'recovered'

interface ThresholdRule {
  id: string
  name: string
  description: string
  metric: string
  unit: string
  comparison: Comparison
  warning_threshold: number
  critical_threshold: number
  hysteresis: number
  consecutive_breaches: number
  cooldown_seconds: number
  escalate_after_seconds: number
  max_tier: number
  silenced_until?: string
  silence_reason: string
  enabled: boolean
  archived_at?: string
  created_at: string
  updated_at: string
}

interface ThresholdState {
  rule_id: string
  level: Level
  pending_level: Level
  pending_streak: number
  last_value?: number
  last_observed_at?: string
  last_transition_at?: string
  last_fired_at?: string
  tier: number
  tier_since?: string
  updated_at: string
}

interface ThresholdRuleView {
  rule: ThresholdRule
  state: ThresholdState
}

interface ThresholdEvent {
  id: string
  rule_id: string
  rule_name: string
  metric: string
  unit: string
  kind: EventKind
  kind_label: string
  from_level: Level
  to_level: Level
  value: number
  threshold: number
  tier: number
  pending_streak: number
  notified: boolean
  suppressed: boolean
  suppression_reason: '' | 'silenced' | 'cooldown'
  message: string
  occurred_at: string
  created_at: string
}

interface ThresholdIngest {
  rule: ThresholdRule
  state: ThresholdState
  previous_level: Level
  level: Level
  changed: boolean
  pending_level: Level
  pending_streak: number
  tier: number
  notified: boolean
  suppressed: boolean
  suppression_reason: '' | 'silenced' | 'cooldown'
  message: string
  event?: ThresholdEvent
}

interface RuleInput {
  name: string
  description: string
  metric: string
  unit: string
  comparison: Comparison
  warning_threshold: number
  critical_threshold: number
  hysteresis: number
  consecutive_breaches: number
  cooldown_seconds: number
  escalate_after_seconds: number
  max_tier: number
  enabled: boolean
}

const levelText: Record<Level, string> = {
  ok: 'ปกติ',
  warning: 'เตือน',
  critical: 'วิกฤต',
}

const levelTone: Record<Level, string> = {
  ok: 'sev-low',
  warning: 'sev-medium',
  critical: 'sev-critical',
}

const kindTone: Record<EventKind, string> = {
  warning: 'sev-medium',
  critical: 'sev-critical',
  escalated: 'sev-high',
  recovered: 'sev-low',
}

const comparisonText: Record<Comparison, string> = {
  above: 'แจ้งเมื่อค่าสูงกว่าเกณฑ์',
  below: 'แจ้งเมื่อค่าต่ำกว่าเกณฑ์',
}

const suppressionText: Record<string, string> = {
  silenced: 'ระงับเพราะช่วงปิดเสียง',
  cooldown: 'ระงับเพราะช่วงพักการแจ้งเตือน',
}

const RULE_KEY = ['threshold-rules']
const EVENT_KEY = ['threshold-events']

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

function formatMetric(value: number | undefined, unit: string): string {
  if (value === undefined) return '—'
  const text = Number.isInteger(value) ? String(value) : value.toFixed(2)
  if (unit === '%') return `${text}%`
  return unit ? `${text} ${unit}` : text
}

function activeSilence(rule: ThresholdRule): boolean {
  if (!rule.silenced_until) return false
  return new Date(rule.silenced_until).getTime() > Date.now()
}

function toLocalInput(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function durationText(seconds: number): string {
  if (seconds === 0) return 'ไม่ยกระดับ'
  if (seconds < 60) return `${seconds} วินาที`
  if (seconds % 3600 === 0) return `${seconds / 3600} ชั่วโมง`
  if (seconds % 60 === 0) return `${seconds / 60} นาที`
  return `${seconds} วินาที`
}

export function Thresholds() {
  const { me } = useAuth()
  const qc = useQueryClient()
  const myRole: Role = me?.role ?? 'auditor'
  const canConfigure = myRole === 'owner' || myRole === 'admin'

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [notice, setNotice] = useState('')
  const [actionError, setActionError] = useState('')
  const [sampleValue, setSampleValue] = useState('85')
  const [sampleAt, setSampleAt] = useState('')
  const [silenceUntil, setSilenceUntil] = useState(() => toLocalInput(new Date(Date.now() + 30 * 60_000)))
  const [silenceReason, setSilenceReason] = useState('ปิดปรับปรุงตามแผน')
  const [lastResult, setLastResult] = useState<ThresholdIngest | null>(null)
  const [eventRule, setEventRule] = useState('')

  const [form, setForm] = useState({
    name: 'ความหน่วง HTTP p95',
    description: '',
    metric: 'core.http.latency_p95',
    unit: 'ms',
    comparison: 'above' as Comparison,
    warning: '300',
    critical: '500',
    hysteresis: '25',
    consecutive: '3',
    cooldown: '300',
    escalate_after: '600',
    max_tier: '3',
  })
  const [formError, setFormError] = useState('')

  const rules = useQuery({
    queryKey: RULE_KEY,
    queryFn: () => list<ThresholdRuleView>('/v1/thresholds/rules'),
    refetchInterval: 15_000,
  })

  const events = useQuery({
    queryKey: [...EVENT_KEY, eventRule],
    queryFn: () => list<ThresholdEvent>(
      `/v1/thresholds/events?limit=100${eventRule ? `&rule_id=${encodeURIComponent(eventRule)}` : ''}`,
    ),
    refetchInterval: 10_000,
  })

  const ruleRows = rules.data ?? []
  const eventRows = events.data ?? []
  const selected = ruleRows.find((row) => row.rule.id === selectedId) ?? ruleRows[0]
  const selectedRule = selected?.rule

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: RULE_KEY })
    void qc.invalidateQueries({ queryKey: EVENT_KEY })
  }

  const createRule = useMutation({
    mutationFn: (input: RuleInput) => apiWrite<ThresholdRuleView>('/v1/thresholds/rules', 'POST', input),
    onSuccess: (view) => {
      setActionError('')
      setNotice(`สร้างกฎ “${view.rule.name}” แล้ว เริ่มที่ระดับปกติ`)
      setSelectedId(view.rule.id)
      void qc.invalidateQueries({ queryKey: RULE_KEY })
      void qc.invalidateQueries({ queryKey: EVENT_KEY })
    },
    onError: (err) => {
      setNotice('')
      setActionError(errorText(err, 'สร้างกฎไม่สำเร็จ'))
    },
  })

  const toggleRule = useMutation({
    mutationFn: (rule: ThresholdRule) => apiWrite<ThresholdRuleView>(
      `/v1/thresholds/rules/${rule.id}`,
      'PATCH',
      { enabled: !rule.enabled },
    ),
    onSuccess: (view) => {
      setActionError('')
      setNotice(`${view.rule.enabled ? 'เปิด' : 'ปิด'}ใช้งานกฎ “${view.rule.name}” แล้ว`)
      void qc.invalidateQueries({ queryKey: RULE_KEY })
    },
    onError: (err) => setActionError(errorText(err, 'เปลี่ยนสถานะกฎไม่สำเร็จ')),
  })

  const archiveRule = useMutation({
    mutationFn: (rule: ThresholdRule) => apiWrite<{ rule: ThresholdRule; archived: boolean }>(
      `/v1/thresholds/rules/${rule.id}`,
      'DELETE',
    ),
    onSuccess: (result) => {
      setActionError('')
      setNotice(`เก็บถาวรกฎ “${result.rule.name}” แล้ว ประวัติเหตุการณ์ยังอยู่ครบ`)
      setSelectedId(null)
      refresh()
    },
    onError: (err) => setActionError(errorText(err, 'เก็บถาวรกฎไม่สำเร็จ')),
  })

  const silenceRule = useMutation({
    mutationFn: (input: { rule: ThresholdRule; until: string | null; reason: string }) => apiWrite<ThresholdRuleView>(
      `/v1/thresholds/rules/${input.rule.id}/silence`,
      'POST',
      { until: input.until, reason: input.reason },
    ),
    onSuccess: (view) => {
      setActionError('')
      if (view.rule.silenced_until) {
        setNotice(`ปิดเสียงกฎ “${view.rule.name}” ถึง ${thaiDateTime(view.rule.silenced_until)} แล้ว`)
      } else {
        setNotice(`เปิดการแจ้งเตือนกฎ “${view.rule.name}” ตามปกติแล้ว`)
      }
      void qc.invalidateQueries({ queryKey: RULE_KEY })
    },
    onError: (err) => setActionError(errorText(err, 'ตั้งช่วงปิดเสียงไม่สำเร็จ')),
  })

  const ingest = useMutation({
    mutationFn: (input: { rule: ThresholdRule; value: number; observedAt: string }) => apiWrite<ThresholdIngest>(
      `/v1/thresholds/rules/${input.rule.id}/samples`,
      'POST',
      {
        value: input.value,
        observed_at: input.observedAt === '' ? undefined : new Date(input.observedAt).toISOString(),
      },
    ),
    onMutate: () => {
      setLastResult(null)
      setNotice('')
      setActionError('')
    },
    onSuccess: (result) => {
      setLastResult(result)
      if (result.event) {
        const delivery = result.event.notified
          ? 'ส่งแจ้งเตือนแล้ว'
          : suppressionText[result.event.suppression_reason] || 'บันทึกโดยไม่ส่งแจ้งเตือน'
        setNotice(`${result.event.kind_label} · ${delivery}`)
      } else if (result.pending_streak > 0) {
        setNotice(`ยังไม่ถึงจำนวนครั้งที่กำหนด (${result.pending_streak}/${result.rule.consecutive_breaches})`)
      } else {
        setNotice(result.message)
      }
      refresh()
    },
    onError: (err) => setActionError(errorText(err, 'ประเมินตัวอย่างไม่สำเร็จ')),
  })

  const activeRules = ruleRows.filter((row) => row.rule.enabled)
  const warningCount = ruleRows.filter((row) => row.state.level === 'warning').length
  const criticalCount = ruleRows.filter((row) => row.state.level === 'critical').length
  const silencedCount = ruleRows.filter((row) => activeSilence(row.rule)).length
  const deliveredCount = eventRows.filter((row) => row.notified).length
  const suppressedCount = eventRows.filter((row) => row.suppressed).length

  function submitRule(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setFormError('')
    setNotice('')
    const warning = Number(form.warning)
    const critical = Number(form.critical)
    const hysteresis = Number(form.hysteresis)
    const consecutive = Number(form.consecutive)
    const cooldown = Number(form.cooldown)
    const escalate = Number(form.escalate_after)
    const maxTier = Number(form.max_tier)

    if (form.name.trim() === '' || form.metric.trim() === '') {
      setFormError('ต้องระบุชื่อกฎและชื่อเมตริก')
      return
    }
    if (![warning, critical, hysteresis, consecutive, cooldown, escalate, maxTier].every(Number.isFinite)) {
      setFormError('ค่าตัวเลขทุกช่องต้องเป็นตัวเลขที่ถูกต้อง')
      return
    }
    if (form.comparison === 'above' ? critical <= warning : critical >= warning) {
      setFormError(form.comparison === 'above'
        ? 'เกณฑ์วิกฤตต้องมากกว่าเกณฑ์เตือน'
        : 'เกณฑ์วิกฤตต้องน้อยกว่าเกณฑ์เตือน')
      return
    }
    if (hysteresis < 0 || hysteresis >= Math.abs(critical - warning)) {
      setFormError('ช่วง hysteresis ต้องไม่ติดลบ และต้องน้อยกว่าระยะห่างระหว่างเกณฑ์เตือนกับเกณฑ์วิกฤต')
      return
    }
    if (maxTier > 1 && escalate <= 0) {
      setFormError('ต้องระบุระยะเวลาก่อนยกระดับเมื่อตั้งระดับสูงสุดมากกว่า 1')
      return
    }

    createRule.mutate({
      name: form.name.trim(),
      description: form.description.trim(),
      metric: form.metric.trim(),
      unit: form.unit.trim(),
      comparison: form.comparison,
      warning_threshold: warning,
      critical_threshold: critical,
      hysteresis,
      consecutive_breaches: consecutive,
      cooldown_seconds: cooldown,
      escalate_after_seconds: escalate,
      max_tier: maxTier,
      enabled: true,
    })
  }

  function submitSample() {
    if (!selectedRule) return
    const value = Number(sampleValue)
    if (!Number.isFinite(value)) {
      setActionError('ค่าที่วัดได้ต้องเป็นตัวเลข')
      return
    }
    setActionError('')
    ingest.mutate({ rule: selectedRule, value, observedAt: sampleAt })
  }

  function submitSilence() {
    if (!selectedRule) return
    const until = new Date(silenceUntil)
    if (Number.isNaN(until.getTime())) {
      setActionError('เวลาสิ้นสุดการปิดเสียงไม่ถูกต้อง')
      return
    }
    setActionError('')
    silenceRule.mutate({ rule: selectedRule, until: until.toISOString(), reason: silenceReason.trim() })
  }

  function clearSilence() {
    if (!selectedRule) return
    setActionError('')
    silenceRule.mutate({ rule: selectedRule, until: null, reason: '' })
  }

  return (
    <>
      <PageHead
        title="เกณฑ์แจ้งเตือนตามค่าที่วัดได้"
        sub="เทียบตัวอย่างเมตริกกับเกณฑ์เตือนและเกณฑ์วิกฤต พร้อมช่วง hysteresis จำนวนครั้งติดกัน ช่วงพักการแจ้งเตือน การยกระดับตามอายุ และช่วงปิดเสียง"
        actions={(
          <button className="btn btn-sm" onClick={refresh} disabled={rules.isFetching || events.isFetching}>
            {rules.isFetching || events.isFetching ? 'กำลังโหลด…' : 'รีเฟรช'}
          </button>
        )}
      />

      <div className="grid-4" style={{ marginTop: 4 }}>
        <Stat label="กฎที่ใช้งาน" value={activeRules.length} unit={`/ ${ruleRows.length}`} foot="นับเฉพาะกฎที่ไม่ถูกเก็บถาวร" />
        <Stat label="ระดับเตือน" value={warningCount} foot="รวมกฎที่ปิดเสียงอยู่" />
        <Stat label="ระดับวิกฤต" value={criticalCount} foot="ต้องการการตรวจสอบทันที" />
        <Stat label="ช่วงปิดเสียง" value={silencedCount} foot="ยังบันทึกเหตุการณ์ครบถ้วน" />
      </div>

      {notice ? (
        <div className="card card-pad" style={{ marginTop: 16, borderColor: 'rgba(46,125,100,0.32)' }}>
          <div style={{ fontSize: 12.5 }}>{notice}</div>
        </div>
      ) : null}
      {actionError ? (
        <div className="card card-pad" style={{ marginTop: 16, borderColor: 'rgba(199,123,43,0.4)' }}>
          <div style={{ fontSize: 12.5, color: 'var(--color-amber)' }}>{actionError}</div>
        </div>
      ) : null}

      {rules.isLoading ? (
        <div className="card card-pad" style={{ marginTop: 16 }}><Skel h={220} /></div>
      ) : ruleRows.length === 0 ? (
        <div className="card card-pad" style={{ marginTop: 16 }}>
          <Empty title="ยังไม่มีกฎแจ้งเตือน" body="สร้างกฎแรกจากแบบฟอร์มด้านล่าง เพื่อเริ่มประเมินตัวอย่างเมตริก" />
        </div>
      ) : (
        <div className="grid-2" style={{ marginTop: 16 }}>
          <div className="card card-pad">
            <div className="row" style={{ alignItems: 'baseline' }}>
              <span className="card-label">คิวตามระดับปัจจุบัน</span>
              <span className="dim" style={{ marginLeft: 'auto', fontSize: 11 }}>
                ระดับมาจากสถานะที่บันทึกไว้ ไม่ได้คำนวณซ้ำบนหน้าจอ
              </span>
            </div>
            <div className="table-wrap" style={{ marginTop: 12 }}>
              <table className="table data">
                <thead>
                  <tr>
                    <th>ระดับ</th>
                    <th>กฎ / เมตริก</th>
                    <th style={{ textAlign: 'right' }}>ค่าล่าสุด</th>
                    <th style={{ textAlign: 'right' }}>รอการยืนยัน</th>
                  </tr>
                </thead>
                <tbody>
                  {ruleRows.map((row) => (
                    <tr
                      key={row.rule.id}
                      onClick={() => setSelectedId(row.rule.id)}
                      style={{
                        cursor: 'pointer',
                        background: row.rule.id === selected?.rule.id ? 'rgba(200,169,106,0.06)' : undefined,
                      }}
                    >
                      <td>
                        <span className={`sev ${levelTone[row.state.level]}`}>
                          <i className="sev-dot" />
                          {levelText[row.state.level]}
                        </span>
                        {!row.rule.enabled ? (
                          <div className="dim" style={{ fontSize: 10.5 }}>ปิดใช้งาน</div>
                        ) : null}
                      </td>
                      <td>
                        <div>{row.rule.name}</div>
                        <div className="dim mono" style={{ fontSize: 10.5 }}>{row.rule.metric}</div>
                      </td>
                      <td className="tnum" style={{ textAlign: 'right' }}>
                        {formatMetric(row.state.last_value, row.rule.unit)}
                        {row.rule.silenced_until && activeSilence(row.rule) ? (
                          <div className="dim" style={{ fontSize: 10.5 }}>ปิดเสียง</div>
                        ) : null}
                      </td>
                      <td className="tnum dim" style={{ textAlign: 'right' }}>
                        {row.state.pending_streak > 0
                          ? `${row.state.pending_streak}/${row.rule.consecutive_breaches}`
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card card-pad">
            {!selected ? (
              <Empty title="ยังไม่ได้เลือกกฎ" body="เลือกแถวจากคิวเพื่อดูรายละเอียดและส่งตัวอย่าง" />
            ) : (
              <>
                <div className="row" style={{ alignItems: 'baseline' }}>
                  <span className="card-label">รายละเอียดกฎ</span>
                  <span className={`sev ${levelTone[selected.state.level]}`} style={{ marginLeft: 'auto' }}>
                    <i className="sev-dot" />
                    {levelText[selected.state.level]}
                  </span>
                </div>
                <div style={{ fontSize: 15, marginTop: 8 }}>{selectedRule?.name}</div>
                <div className="dim mono" style={{ fontSize: 10.5, marginTop: 4 }}>
                  {selectedRule?.metric} {selectedRule?.unit ? `· ${selectedRule.unit}` : ''}
                </div>
                {selectedRule?.description ? (
                  <div className="dim" style={{ fontSize: 11.5, marginTop: 6 }}>{selectedRule.description}</div>
                ) : null}

                <div className="grid-2" style={{ marginTop: 14, gap: 10 }}>
                  <div>
                    <div className="dim" style={{ fontSize: 10.5 }}>เกณฑ์เตือน</div>
                    <div className="tnum" style={{ fontSize: 20 }}>{formatMetric(selectedRule?.warning_threshold, selectedRule?.unit ?? '')}</div>
                  </div>
                  <div>
                    <div className="dim" style={{ fontSize: 10.5 }}>เกณฑ์วิกฤต</div>
                    <div className="tnum" style={{ fontSize: 20, color: 'var(--color-amber)' }}>
                      {formatMetric(selectedRule?.critical_threshold, selectedRule?.unit ?? '')}
                    </div>
                  </div>
                </div>

                <div className="stack" style={{ marginTop: 12, gap: 6, fontSize: 11.5 }}>
                  <div className="row"><span className="dim">ทิศทาง</span><span style={{ marginLeft: 'auto' }}>{comparisonText[selectedRule?.comparison ?? 'above']}</span></div>
                  <div className="row"><span className="dim">ช่วง hysteresis</span><span className="tnum" style={{ marginLeft: 'auto' }}>{formatMetric(selectedRule?.hysteresis, selectedRule?.unit ?? '')}</span></div>
                  <div className="row"><span className="dim">จำนวนครั้งติดกัน</span><span className="tnum" style={{ marginLeft: 'auto' }}>{selectedRule?.consecutive_breaches} ครั้ง</span></div>
                  <div className="row"><span className="dim">ช่วงพักการแจ้งเตือน</span><span style={{ marginLeft: 'auto' }}>{durationText(selectedRule?.cooldown_seconds ?? 0)}</span></div>
                  <div className="row"><span className="dim">ยกระดับสูงสุด</span><span style={{ marginLeft: 'auto' }}>ระดับ {selectedRule?.max_tier} · ทุก {durationText(selectedRule?.escalate_after_seconds ?? 0)}</span></div>
                  <div className="row"><span className="dim">ค่าล่าสุด</span><span className="tnum" style={{ marginLeft: 'auto' }}>{formatMetric(selected.state.last_value, selectedRule?.unit ?? '')}</span></div>
                  <div className="row"><span className="dim">ระดับขั้นที่รอ</span><span style={{ marginLeft: 'auto' }}>{selected.state.pending_streak > 0 ? `${levelText[selected.state.pending_level]} · ${selected.state.pending_streak}/${selectedRule?.consecutive_breaches}` : 'ไม่มี'}</span></div>
                  <div className="row"><span className="dim">ระดับการยกระดับ</span><span style={{ marginLeft: 'auto' }}>ระดับ {selected.state.tier}</span></div>
                  <div className="row"><span className="dim">แจ้งเตือนล่าสุด</span><span style={{ marginLeft: 'auto' }}>{selected.state.last_fired_at ? thaiDateTime(selected.state.last_fired_at) : 'ยังไม่เคย'}</span></div>
                </div>

                {selectedRule?.silenced_until && activeSilence(selectedRule) ? (
                  <div style={{
                    marginTop: 12,
                    border: '1px solid rgba(199,123,43,0.32)',
                    borderRadius: 12,
                    padding: 10,
                    fontSize: 11.5,
                  }}>
                    ปิดเสียงถึง {thaiDateTime(selectedRule.silenced_until)}
                    {selectedRule.silence_reason ? ` · ${selectedRule.silence_reason}` : ''}
                    <div className="dim" style={{ marginTop: 4 }}>
                      ช่วงนี้ยังบันทึกทุกการละเมิด แต่จะไม่ส่งแจ้งเตือน
                    </div>
                  </div>
                ) : null}

                <div className="field" style={{ marginTop: 14 }}>
                  <label htmlFor="threshold-sample">ค่าที่วัดได้ ({selectedRule?.unit || 'ไม่มีหน่วย'})</label>
                  <input
                    id="threshold-sample"
                    className="input"
                    inputMode="decimal"
                    value={sampleValue}
                    onChange={(event) => setSampleValue(event.target.value)}
                  />
                </div>
                <div className="field">
                  <label htmlFor="threshold-sample-at">เวลาที่วัด (ไม่บังคับ)</label>
                  <input
                    id="threshold-sample-at"
                    className="input"
                    type="datetime-local"
                    value={sampleAt}
                    onChange={(event) => setSampleAt(event.target.value)}
                  />
                </div>
                <div className="row" style={{ marginTop: 10 }}>
                  <button
                    className="btn btn-primary btn-sm"
                    disabled={ingest.isPending || !selectedRule?.enabled}
                    onClick={submitSample}
                  >
                    {ingest.isPending ? 'กำลังประเมิน…' : 'ส่งตัวอย่างและประเมิน'}
                  </button>
                  <button
                    className="btn btn-sm"
                    disabled={toggleRule.isPending || !canConfigure}
                    onClick={() => selectedRule && toggleRule.mutate(selectedRule)}
                  >
                    {selectedRule?.enabled ? 'ปิดใช้งานกฎ' : 'เปิดใช้งานกฎ'}
                  </button>
                </div>

                {lastResult && lastResult.rule.id === selectedRule?.id ? (
                  <div style={{
                    marginTop: 14,
                    border: '1px solid var(--color-hairline)',
                    borderRadius: 12,
                    padding: 12,
                  }}>
                    <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                      <span className={`sev ${levelTone[lastResult.level]}`}>
                        <i className="sev-dot" />
                        {levelText[lastResult.level]}
                      </span>
                      <span className="chip">{lastResult.changed ? 'ระดับเปลี่ยน' : 'ระดับเดิม'}</span>
                      {lastResult.event ? (
                        <span className="chip">{lastResult.event.notified ? 'แจ้งเตือนแล้ว' : suppressionText[lastResult.event.suppression_reason] || 'บันทึกเท่านั้น'}</span>
                      ) : null}
                    </div>
                    <div style={{ fontSize: 12, marginTop: 8, lineHeight: 1.7 }}>{lastResult.message}</div>
                    <div className="dim" style={{ fontSize: 10.5, marginTop: 6 }}>
                      {lastResult.previous_level} → {lastResult.level} · รอ {lastResult.pending_streak}/{lastResult.rule.consecutive_breaches} · ระดับยกระดับ {lastResult.tier}
                    </div>
                  </div>
                ) : null}

                {canConfigure ? (
                  <div style={{ marginTop: 16, borderTop: '1px solid var(--color-hairline)', paddingTop: 12 }}>
                    <div className="card-label">ช่วงปิดเสียง</div>
                    <div className="field">
                      <label htmlFor="threshold-silence-until">ปิดเสียงถึง</label>
                      <input
                        id="threshold-silence-until"
                        className="input"
                        type="datetime-local"
                        value={silenceUntil}
                        onChange={(event) => setSilenceUntil(event.target.value)}
                      />
                    </div>
                    <div className="field">
                      <label htmlFor="threshold-silence-reason">เหตุผล</label>
                      <input
                        id="threshold-silence-reason"
                        className="input"
                        value={silenceReason}
                        onChange={(event) => setSilenceReason(event.target.value)}
                        placeholder="เช่น ซ่อมบำรุงตามแผน"
                      />
                    </div>
                    <div className="row" style={{ marginTop: 8 }}>
                      <button className="btn btn-sm" disabled={silenceRule.isPending} onClick={submitSilence}>
                        {silenceRule.isPending ? 'กำลังบันทึก…' : 'เริ่มช่วงปิดเสียง'}
                      </button>
                      {selectedRule?.silenced_until ? (
                        <button className="btn btn-sm" disabled={silenceRule.isPending} onClick={clearSilence}>
                          เปิดแจ้งเตือนทันที
                        </button>
                      ) : null}
                    </div>
                  </div>
                ) : null}

                {canConfigure ? (
                  <div className="row" style={{ marginTop: 16 }}>
                    <button
                      className="btn btn-sm"
                      style={{ color: 'var(--color-amber)' }}
                      disabled={archiveRule.isPending}
                      onClick={() => {
                        if (selectedRule && window.confirm(`เก็บถาวรกฎ “${selectedRule.name}” ? ประวัติเหตุการณ์จะยังอยู่ แต่กฎจะหายจากคิว`)) {
                          archiveRule.mutate(selectedRule)
                        }
                      }}
                    >
                      เก็บถาวรกฎ
                    </button>
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
      )}

      <div className="card card-pad" style={{ marginTop: 16 }}>
        <div className="row" style={{ alignItems: 'baseline' }}>
          <span className="card-label">บันทึกเหตุการณ์เกณฑ์</span>
          <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
            {deliveredCount} แจ้งเตือน · {suppressedCount} ระงับ · ใหม่สุดก่อน
          </span>
        </div>
        <div className="field" style={{ maxWidth: 320, marginTop: 10 }}>
          <label htmlFor="threshold-event-rule">กรองตามกฎ</label>
          <select
            id="threshold-event-rule"
            className="input"
            value={eventRule}
            onChange={(event) => setEventRule(event.target.value)}
          >
            <option value="">ทุกกฎ</option>
            {ruleRows.map((row) => (
              <option key={row.rule.id} value={row.rule.id}>{row.rule.name}</option>
            ))}
          </select>
        </div>
        {events.isLoading ? (
          <Skel h={140} />
        ) : eventRows.length === 0 ? (
          <Empty title="ยังไม่มีเหตุการณ์" body="ส่งตัวอย่างที่ผ่านเกณฑ์เพื่อสร้างบันทึกแรก" />
        ) : (
          <div className="table-wrap" style={{ marginTop: 10 }}>
            <table className="table data">
              <thead>
                <tr>
                  <th>เวลา</th>
                  <th>ประเภท</th>
                  <th>กฎ</th>
                  <th style={{ textAlign: 'right' }}>ค่า / เกณฑ์</th>
                  <th>การส่ง</th>
                  <th>ข้อความ</th>
                </tr>
              </thead>
              <tbody>
                {eventRows.map((row) => (
                  <tr key={row.id}>
                    <td className="dim tnum" style={{ fontSize: 11 }}>{thaiDateTime(row.occurred_at)}</td>
                    <td>
                      <span className={`sev ${kindTone[row.kind]}`}>
                        <i className="sev-dot" />
                        {row.kind_label}
                      </span>
                      {row.tier > 1 ? <div className="dim" style={{ fontSize: 10.5 }}>ระดับ {row.tier}</div> : null}
                    </td>
                    <td>
                      <div>{row.rule_name}</div>
                      <div className="dim mono" style={{ fontSize: 10.5 }}>{row.metric}</div>
                    </td>
                    <td className="tnum" style={{ textAlign: 'right' }}>
                      {formatMetric(row.value, row.unit)}
                      <div className="dim" style={{ fontSize: 10.5 }}>เกณฑ์ {formatMetric(row.threshold, row.unit)}</div>
                    </td>
                    <td>
                      <span className={`sev ${row.notified ? 'sev-low' : 'sev-medium'}`}>
                        <i className="sev-dot" />
                        {row.notified ? 'ส่งแล้ว' : 'ระงับ'}
                      </span>
                      {row.suppressed && row.suppression_reason ? (
                        <div className="dim" style={{ fontSize: 10.5 }}>{suppressionText[row.suppression_reason]}</div>
                      ) : null}
                    </td>
                    <td style={{ fontSize: 11.5, maxWidth: 360 }}>{row.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="dim" style={{ fontSize: 11, marginTop: 10 }}>
          ตารางนี้เป็นบันทึกแบบเพิ่มอย่างเดียว: การปิดเสียงและการพักการแจ้งเตือนลดการส่งข้อความ ไม่ได้ลบหลักฐานการละเมิด
        </div>
      </div>

      <div className="card card-pad" style={{ marginTop: 16 }}>
        <div className="row" style={{ alignItems: 'baseline' }}>
          <span className="card-label">สร้างกฎแจ้งเตือน</span>
          <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
            เกณฑ์เป็นหน่วยเดียวกับเมตริก และใช้ได้ทั้งค่าสูงและค่าต่ำ
          </span>
        </div>
        {!canConfigure ? (
          <div className="dim" style={{ fontSize: 12, marginTop: 10 }}>
            การสร้างและแก้ไขกฎทำได้โดยเจ้าของหรือผู้ดูแลระบบเท่านั้น
          </div>
        ) : (
          <form onSubmit={submitRule} style={{ marginTop: 12 }}>
            <div className="grid-2">
              <div className="field">
                <label htmlFor="threshold-name">ชื่อกฎ</label>
                <input
                  id="threshold-name"
                  className="input"
                  value={form.name}
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                />
              </div>
              <div className="field">
                <label htmlFor="threshold-metric">ชื่อเมตริก</label>
                <input
                  id="threshold-metric"
                  className="input mono"
                  value={form.metric}
                  onChange={(event) => setForm({ ...form, metric: event.target.value })}
                  placeholder="core.http.latency_p95"
                />
              </div>
              <div className="field">
                <label htmlFor="threshold-unit">หน่วย</label>
                <input
                  id="threshold-unit"
                  className="input"
                  value={form.unit}
                  onChange={(event) => setForm({ ...form, unit: event.target.value })}
                  placeholder="ms, %, count"
                />
              </div>
              <div className="field">
                <label htmlFor="threshold-comparison">ทิศทางเกณฑ์</label>
                <select
                  id="threshold-comparison"
                  className="input"
                  value={form.comparison}
                  onChange={(event) => setForm({ ...form, comparison: event.target.value as Comparison })}
                >
                  <option value="above">ค่าสูงกว่าเกณฑ์ (above)</option>
                  <option value="below">ค่าต่ำกว่าเกณฑ์ (below)</option>
                </select>
              </div>
              <div className="field">
                <label htmlFor="threshold-warning">เกณฑ์เตือน</label>
                <input
                  id="threshold-warning"
                  className="input"
                  inputMode="decimal"
                  value={form.warning}
                  onChange={(event) => setForm({ ...form, warning: event.target.value })}
                />
              </div>
              <div className="field">
                <label htmlFor="threshold-critical">เกณฑ์วิกฤต</label>
                <input
                  id="threshold-critical"
                  className="input"
                  inputMode="decimal"
                  value={form.critical}
                  onChange={(event) => setForm({ ...form, critical: event.target.value })}
                />
              </div>
              <div className="field">
                <label htmlFor="threshold-hysteresis">ช่วง hysteresis</label>
                <input
                  id="threshold-hysteresis"
                  className="input"
                  inputMode="decimal"
                  value={form.hysteresis}
                  onChange={(event) => setForm({ ...form, hysteresis: event.target.value })}
                />
              </div>
              <div className="field">
                <label htmlFor="threshold-consecutive">จำนวนครั้งติดกัน</label>
                <input
                  id="threshold-consecutive"
                  className="input"
                  inputMode="numeric"
                  value={form.consecutive}
                  onChange={(event) => setForm({ ...form, consecutive: event.target.value })}
                />
              </div>
              <div className="field">
                <label htmlFor="threshold-cooldown">ช่วงพักการแจ้งเตือน (วินาที)</label>
                <input
                  id="threshold-cooldown"
                  className="input"
                  inputMode="numeric"
                  value={form.cooldown}
                  onChange={(event) => setForm({ ...form, cooldown: event.target.value })}
                />
              </div>
              <div className="field">
                <label htmlFor="threshold-escalate">ยกระดับทุก (วินาที)</label>
                <input
                  id="threshold-escalate"
                  className="input"
                  inputMode="numeric"
                  value={form.escalate_after}
                  onChange={(event) => setForm({ ...form, escalate_after: event.target.value })}
                />
              </div>
              <div className="field">
                <label htmlFor="threshold-max-tier">ระดับยกระดับสูงสุด</label>
                <select
                  id="threshold-max-tier"
                  className="input"
                  value={form.max_tier}
                  onChange={(event) => setForm({ ...form, max_tier: event.target.value })}
                >
                  <option value="1">ระดับ 1 (ไม่ยกระดับ)</option>
                  <option value="2">ระดับ 2</option>
                  <option value="3">ระดับ 3</option>
                </select>
              </div>
              <div className="field">
                <label htmlFor="threshold-description">คำอธิบาย</label>
                <input
                  id="threshold-description"
                  className="input"
                  value={form.description}
                  onChange={(event) => setForm({ ...form, description: event.target.value })}
                  placeholder="ไม่บังคับ"
                />
              </div>
            </div>
            {formError ? <div className="form-error">{formError}</div> : null}
            <button className="btn btn-primary" type="submit" disabled={createRule.isPending} style={{ marginTop: 12 }}>
              {createRule.isPending ? 'กำลังสร้าง…' : 'สร้างกฎ'}
            </button>
          </form>
        )}
      </div>
    </>
  )
}
